/**
 * Ações sobre a conversa no cabeçalho: assumir (um clique), e um menu com
 * transferir, acompanhar, etiquetas, prioridade e devolver à fila. Tudo
 * interno: a cliente não vê.
 */
import type { ConversaResumo } from "@ydchat/shared";
import {
  ETAPAS_CONVERSA,
  ROTULO_ETAPA,
  ROTULO_PRESENCA,
  ROTULO_PRIORIDADE,
  veOperacaoInteira,
} from "@ydchat/shared";
import {
  ArrowLeft,
  ArrowRightLeft,
  BellOff,
  BellRing,
  Bot,
  KanbanSquare,
  Clock,
  Flag,
  Hand,
  Inbox,
  MoreHorizontal,
  Shuffle,
  Tag,
} from "lucide-react";
import { useState } from "react";
import { Avatar } from "@/components/ui/avatar";
import { Botao } from "@/components/ui/botao";
import { Campo } from "@/components/ui/campo";
import { ItemMenu, Popover } from "@/components/ui/popover";
import { useAcoesCentral, useEtiquetas } from "@/lib/central";
import { formatarHora, horaOuData } from "@/lib/formatar";
import { cn } from "@/lib/cn";
import { COR_PRESENCA, useAcoesConversa, useUsuariosResumo } from "@/lib/equipe";
import { useUsuario } from "@/lib/sessao";
import { useAcoesPonte, useConfigAtendimento } from "@/lib/atendimento";
import { useMudarEtapa } from "@/lib/automacoes";

export function BotaoAssumir({
  conversa,
  compacto = false,
}: {
  conversa: ConversaResumo;
  compacto?: boolean;
}) {
  const { assumir } = useAcoesConversa();
  if (conversa.responsavel) return null;
  return (
    <Botao
      tamanho="sm"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        assumir.mutate(conversa.id);
      }}
      disabled={assumir.isPending}
      className={compacto ? "h-7 px-2.5 text-xs" : undefined}
    >
      <Hand size={15} strokeWidth={1.75} />
      Assumir
    </Botao>
  );
}

export function MenuAcoesConversa({ conversa }: { conversa: ConversaResumo }) {
  const usuario = useUsuario();
  const gestor = veOperacaoInteira(usuario.papel);
  const minha = conversa.responsavel?.id === usuario.id;
  const { transferir, retirar, prioridade } = useAcoesConversa();
  const { acompanhar } = useAcoesCentral();
  const [modo, setModo] = useState<
    "menu" | "transferir" | "prioridade" | "acompanhar" | "etiquetas" | "etapa"
  >("menu");
  const [aberto, setAberto] = useState(false);
  const podeTransferir = gestor || minha;
  const ponte = useAcoesPonte(conversa.id);
  const etapa = useMudarEtapa();
  const atendimento = useConfigAtendimento().data;
  const pontePossivel = !!atendimento?.iaDisponivel && !!atendimento.ponte.ativo;

  const mudarAberto = (v: boolean) => {
    setAberto(v);
    if (!v) setModo("menu");
  };

  return (
    <Popover
      aberto={aberto}
      onAberto={mudarAberto}
      largura="w-80"
      gatilho={({ alternar, aberto }) => (
        <Botao
          variante="fantasma"
          tamanho="icone"
          onClick={alternar}
          aria-label="Mais ações"
          aria-expanded={aberto}
          title="Mais ações"
        >
          <MoreHorizontal size={20} strokeWidth={1.75} />
        </Botao>
      )}
    >
      {(fechar) =>
        modo === "transferir" ? (
          <EscolherPessoa
            excluir={conversa.responsavel?.id ?? null}
            onVoltar={() => setModo("menu")}
            onEscolher={(p, motivo) => {
              transferir.mutate({ id: conversa.id, usuarioId: p.id, nome: p.nome, motivo });
              fechar();
            }}
          />
        ) : modo === "acompanhar" ? (
          <div>
            <Voltar onClick={() => setModo("menu")} titulo="Lembrar de acompanhar" />
            <EscolherLembrete
              atual={conversa.acompanharEm}
              onEscolher={(em, nota) => {
                acompanhar.mutate({ id: conversa.id, em, nota });
                fechar();
              }}
            />
          </div>
        ) : modo === "etiquetas" ? (
          <div>
            <Voltar onClick={() => setModo("menu")} titulo="Etiquetas" />
            <EscolherEtiquetas conversa={conversa} />
          </div>
        ) : modo === "etapa" ? (
          <div className="rolagem-fina max-h-96 overflow-y-auto">
            <Voltar onClick={() => setModo("menu")} titulo="Etapa do funil" />
            {ETAPAS_CONVERSA.filter((e) => e !== "finalizado").map((e) => (
              <ItemMenu
                key={e}
                icone={KanbanSquare}
                onClick={() => {
                  etapa.mutate({ conversaId: conversa.id, etapa: e });
                  fechar();
                }}
              >
                {ROTULO_ETAPA[e]}
                {conversa.etapa === e && (
                  <span className="ml-2 text-xs font-normal text-gold-700">atual</span>
                )}
              </ItemMenu>
            ))}
          </div>
        ) : modo === "prioridade" ? (
          <div>
            <Voltar onClick={() => setModo("menu")} titulo="Prioridade" />
            {ROTULO_PRIORIDADE.map((rotulo, i) => (
              <ItemMenu
                key={rotulo}
                icone={Flag}
                onClick={() => {
                  prioridade.mutate({ id: conversa.id, prioridade: i });
                  fechar();
                }}
              >
                {rotulo}
                {conversa.prioridade === i && (
                  <span className="ml-2 text-xs font-normal text-gold-700">atual</span>
                )}
              </ItemMenu>
            ))}
          </div>
        ) : (
          <div>
            {podeTransferir && (
              <ItemMenu
                icone={ArrowRightLeft}
                onClick={() => setModo("transferir")}
                descricao="Escolher outra pessoa da equipe"
              >
                {conversa.responsavel ? "Transferir" : "Atribuir"}
              </ItemMenu>
            )}
            <ItemMenu
              icone={BellRing}
              onClick={() => setModo("acompanhar")}
              descricao={
                conversa.acompanharEm
                  ? `Marcado para ${horaOuData(conversa.acompanharEm)} ${formatarHora(conversa.acompanharEm)}`
                  : "Receber um lembrete sobre esta cliente"
              }
            >
              Acompanhar
            </ItemMenu>
            <ItemMenu
              icone={Tag}
              onClick={() => setModo("etiquetas")}
              descricao={conversa.etiquetas.map((e) => e.nome).join(", ") || "Nenhuma"}
            >
              Etiquetas
            </ItemMenu>
            {(gestor || minha || !conversa.responsavel) && conversa.status !== "encerrada" && (
              <ItemMenu
                icone={KanbanSquare}
                onClick={() => setModo("etapa")}
                descricao={`Agora: ${ROTULO_ETAPA[conversa.etapa]}`}
              >
                Etapa do funil
              </ItemMenu>
            )}
            {(gestor || minha) && (
              <ItemMenu
                icone={Flag}
                onClick={() => setModo("prioridade")}
                descricao={`Agora: ${ROTULO_PRIORIDADE[conversa.prioridade] ?? "Normal"}`}
              >
                Prioridade
              </ItemMenu>
            )}
            {pontePossivel &&
              (gestor || minha || !conversa.responsavel) &&
              conversa.status !== "encerrada" && (
                <ItemMenu
                  icone={Bot}
                  descricao={
                    conversa.modo === "ponte"
                      ? "A IA sai e não volta nesta conversa"
                      : conversa.ponteDesligada
                        ? "A IA conversa com a cliente enquanto a equipe não responde"
                        : "A IA não entra nesta conversa"
                  }
                  onClick={() => {
                    if (conversa.ponteDesligada && conversa.modo !== "ponte") ponte.ligar.mutate();
                    else ponte.assumir.mutate();
                    fechar();
                  }}
                >
                  {conversa.modo === "ponte"
                    ? "Assumir da IA"
                    : conversa.ponteDesligada
                      ? "Ligar Modo Ponte"
                      : "Desligar Modo Ponte aqui"}
                </ItemMenu>
              )}
            {gestor && conversa.responsavel && (
              <>
                <ItemMenu
                  icone={Shuffle}
                  descricao="Distribuição automática escolhe outra vendedora"
                  onClick={() => {
                    retirar.mutate({ id: conversa.id, redistribuir: true });
                    fechar();
                  }}
                >
                  Passar para outra vendedora
                </ItemMenu>
                <ItemMenu
                  icone={Inbox}
                  descricao="Fica sem responsável até alguém assumir"
                  onClick={() => {
                    retirar.mutate({ id: conversa.id, redistribuir: false });
                    fechar();
                  }}
                >
                  Devolver à fila
                </ItemMenu>
              </>
            )}
          </div>
        )
      }
    </Popover>
  );
}

function Voltar({ onClick, titulo }: { onClick: () => void; titulo: string }) {
  return (
    <div className="mb-1 flex items-center gap-1 border-b border-neutral-100 pb-1.5">
      <button
        onClick={onClick}
        aria-label="Voltar"
        className="flex size-8 items-center justify-center rounded-md text-neutral-600 hover:bg-neutral-100"
      >
        <ArrowLeft size={16} strokeWidth={1.75} />
      </button>
      <span className="text-sm font-semibold text-neutral-900">{titulo}</span>
    </div>
  );
}

/** Lista da equipe com status e carga, disponíveis primeiro. */
export function EscolherPessoa({
  excluir,
  onEscolher,
  onVoltar,
  semMotivo = false,
}: {
  excluir: string | null;
  onEscolher: (p: { id: string; nome: string }, motivo?: string) => void;
  onVoltar?: () => void;
  /** Esconde o campo de motivo e a própria pessoa (encaminhamento interno). */
  semMotivo?: boolean;
}) {
  const eu = useUsuario();
  const pessoas = useUsuariosResumo();
  const [motivo, setMotivo] = useState("");
  const ordem = { disponivel: 0, ocupada: 1, pausa: 2, offline: 3 } as const;
  const lista = (pessoas.data ?? [])
    .filter((p) => p.id !== excluir && !(semMotivo && p.id === eu.id))
    .sort(
      (a, b) =>
        ordem[a.statusPresenca] - ordem[b.statusPresenca] ||
        a.atendimentosAbertos - b.atendimentosAbertos,
    );

  return (
    <div>
      {onVoltar && <Voltar onClick={onVoltar} titulo="Transferir para" />}
      <div className={cn("px-1 pb-2", semMotivo && "hidden")}>
        <Campo
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Motivo (opcional, só a equipe vê)"
          className="h-9 text-[13px]"
          maxLength={200}
        />
      </div>
      <ul className="rolagem-fina max-h-72 overflow-y-auto">
        {pessoas.isPending && (
          <li className="px-2.5 py-2 text-sm text-neutral-500">Carregando equipe…</li>
        )}
        {lista.map((p) => (
          <li key={p.id}>
            <button
              onClick={() => onEscolher(p, motivo.trim() || undefined)}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left hover:bg-neutral-100"
            >
              <span className="relative">
                <Avatar nome={p.nome} tamanho="sm" />
                <span
                  className={cn(
                    "absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-white",
                    COR_PRESENCA[p.statusPresenca],
                  )}
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-neutral-900">
                  {p.nome}
                </span>
                <span className="block text-xs text-neutral-500">
                  {ROTULO_PRESENCA[p.statusPresenca]} · {p.atendimentosAbertos} em atendimento
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Atalhos de lembrete + data livre. Horários no fuso do navegador. */
function EscolherLembrete({
  atual,
  onEscolher,
}: {
  atual: string | null;
  onEscolher: (em: string | null, nota?: string) => void;
}) {
  const [nota, setNota] = useState("");
  const [livre, setLivre] = useState("");
  const em = (fn: (d: Date) => void) => {
    const d = new Date();
    fn(d);
    onEscolher(d.toISOString(), nota.trim() || undefined);
  };
  const amanha10 = (d: Date) => {
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
  };
  return (
    <div className="space-y-1">
      <div className="px-1 pb-1">
        <Campo
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="Nota (opcional)"
          className="h-9 text-[13px]"
          maxLength={300}
        />
      </div>
      <ItemMenu icone={Clock} onClick={() => em((d) => d.setHours(d.getHours() + 1))}>
        Daqui a 1 hora
      </ItemMenu>
      <ItemMenu icone={Clock} onClick={() => em((d) => d.setHours(d.getHours() + 3))}>
        Daqui a 3 horas
      </ItemMenu>
      <ItemMenu icone={Clock} onClick={() => em(amanha10)}>
        Amanhã às 10h
      </ItemMenu>
      <ItemMenu
        icone={Clock}
        onClick={() =>
          em((d) => {
            d.setDate(d.getDate() + 3);
            d.setHours(10, 0, 0, 0);
          })
        }
      >
        Em 3 dias, às 10h
      </ItemMenu>
      <div className="flex items-center gap-2 px-1 pt-1">
        <input
          type="datetime-local"
          aria-label="Data e hora do lembrete"
          value={livre}
          onChange={(e) => setLivre(e.target.value)}
          className="h-9 flex-1 rounded-md border border-neutral-200 px-2 text-[13px]"
        />
        <Botao
          tamanho="sm"
          disabled={!livre}
          onClick={() => onEscolher(new Date(livre).toISOString(), nota.trim() || undefined)}
        >
          Marcar
        </Botao>
      </div>
      {atual && (
        <ItemMenu icone={BellOff} perigo onClick={() => onEscolher(null)}>
          Remover lembrete
        </ItemMenu>
      )}
    </div>
  );
}

function EscolherEtiquetas({ conversa }: { conversa: ConversaResumo }) {
  const etiquetas = useEtiquetas();
  const { etiquetas: salvar } = useAcoesCentral();
  const [marcadas, setMarcadas] = useState(() => new Set(conversa.etiquetas.map((e) => e.id)));
  const alternar = (id: string) => {
    const nova = new Set(marcadas);
    if (nova.has(id)) nova.delete(id);
    else nova.add(id);
    setMarcadas(nova);
    salvar.mutate({ id: conversa.id, etiquetaIds: [...nova] });
  };
  const daLoja = (etiquetas.data ?? []).filter((e) => e.empresaId === conversa.empresaId);
  if (!daLoja.length)
    return <p className="px-2.5 py-2 text-sm text-neutral-500">Nenhuma etiqueta cadastrada.</p>;
  return (
    <ul className="rolagem-fina max-h-72 overflow-y-auto">
      {daLoja.map((e) => (
        <li key={e.id}>
          <label className="flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm hover:bg-neutral-100">
            <input
              type="checkbox"
              checked={marcadas.has(e.id)}
              onChange={() => alternar(e.id)}
              className="size-4 accent-[var(--color-gold-500)]"
            />
            <span className="size-2.5 rounded-full" style={{ backgroundColor: e.cor }} />
            {e.nome}
          </label>
        </li>
      ))}
    </ul>
  );
}
